import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { useReactToPrint } from 'react-to-print';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import { closeDialog } from 'app/store/fuse/dialogSlice';
import SkuLabels from './SkuLabel';
import { labelProducts } from './skuLabelRules';

export const SKU_LABEL_PAGE_STYLE = '@page { size: 6in 4in; margin: 0; } body { margin: 0; }';

/**
 * PGSS-5869: the company name and address for the top of the label, from the same settings the box label prints
 * (category "invoice", Settings → Company Profile). GET /api/setting returns each field lowercased.
 */
export async function fetchLabelCompany(get = axios.get) {
  try {
    const { data } = await get('/api/setting', { params: { category: 'invoice' } });
    return { name: data?.['company name'] || '', address: data?.['company address'] || '' };
  } catch {
    return { name: '', address: '' };
  }
}

/** PGSS-5747: preview and print SKU labels for the chosen products (6" wide × 4" tall, one per page). */
function SkuLabelsDialog({ products }) {
  const dispatch = useDispatch();
  const contentRef = useRef(null);
  const [company, setCompany] = useState({ name: '', address: '' });
  useEffect(() => {
    let live = true;
    fetchLabelCompany().then((c) => { if (live) setCompany(c); });
    return () => { live = false; };
  }, []);
  const printable = labelProducts(products);
  const skipped = (products || []).length - printable.length;
  const print = useReactToPrint({ contentRef, pageStyle: SKU_LABEL_PAGE_STYLE, documentTitle: 'SKU labels' });
  return (
    <>
      <DialogTitle>Print SKU labels</DialogTitle>
      <DialogContent>
        {skipped > 0 && (
          <Typography className="mb-12" data-testid="sku-labels-skipped">
            {skipped} product(s) have no SKU and get no label.
          </Typography>
        )}
        <div style={{ maxHeight: '60vh', overflow: 'auto', background: '#eee', padding: 8 }}>
          <SkuLabels ref={contentRef} products={printable} companyName={company.name} companyAddress={company.address} />
        </div>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={() => dispatch(closeDialog())}>
          Close
        </Button>
        <Button
          variant="contained"
          color="secondary"
          disabled={!printable.length}
          onClick={() => print()}
          data-testid="sku-labels-print"
        >
          Print {printable.length} label(s)
        </Button>
      </DialogActions>
    </>
  );
}

export default SkuLabelsDialog;
